// Сводный индекс сезонов: season -> league -> sport, чтобы страницы не собирали его по отдельности.
import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { engineApi, unwrap } from "./client";
import { keys, useLeagues, useSeasons, type Gameweek, type League, type Season } from "./queries";

export interface SeasonInfo {
  season: Season;
  league: League;
  sport: string;
  label: string;
}

export function useSeasonIndex() {
  const seasons = useSeasons();
  const leagues = useLeagues();
  const index = useMemo(() => {
    const byLeague = new Map((leagues.data ?? []).map((l) => [l.id, l]));
    const out = new Map<string, SeasonInfo>();
    for (const s of seasons.data ?? []) {
      const league = byLeague.get(s.league_id);
      if (!league) continue;
      out.set(s.id, {
        season: s,
        league,
        sport: league.sport_code,
        label: `${league.name} ${s.year}/${String(s.year + 1).slice(2)}`,
      });
    }
    return out;
  }, [seasons.data, leagues.data]);
  return {
    index,
    isLoading: seasons.isLoading || leagues.isLoading,
    error: seasons.error ?? leagues.error,
  };
}

/** Туры нескольких сезонов сразу (для карточек команд на главной) */
export function useGameweeksFor(seasonIds: string[]) {
  const results = useQueries({
    queries: seasonIds.map((id) => ({
      queryKey: keys.gameweeks(id),
      queryFn: async () =>
        unwrap(
          await engineApi.GET("/api/v1/seasons/{season_id}/gameweeks", {
            params: { path: { season_id: id } },
          }),
        ),
      staleTime: 3600 * 1000,
    })),
  });
  return useMemo(() => {
    const out = new Map<string, Gameweek[]>();
    seasonIds.forEach((id, i) => {
      const d = results[i]?.data;
      if (d) out.set(id, d);
    });
    return out;
  }, [seasonIds, results]);
}

/** Ближайший открытый тур сезона */
export function nextGameweek(gws: Gameweek[] | undefined, now = Date.now()): Gameweek | undefined {
  return gws?.find((g) => g.status === "open" && new Date(g.deadline_at).getTime() > now);
}
